import { sha256 } from 'js-sha256'

export const getValidationPdfUrl = (pdbId: string): string => {
  if (!pdbId) { return '' }
  const lowerPdbId = pdbId.toLowerCase()
  const middleTwoChars = lowerPdbId.substring(1, 3)
  const proxy = new URL('https://proxy-rcsb.openaidev.workers.dev')
  proxy.searchParams.set('url', `https://files.wwpdb.org/pub/pdb/validation_reports/${middleTwoChars}/${lowerPdbId}/${lowerPdbId}_validation.pdf`);
  return proxy.toString();
}


export const extraThinkCode = (md: string) => {
  const regex = /<juchats-thinking>([\s\S]*?)<\/juchats-thinking>/g
  const matches: string[] = []

  let result = md.replace(regex, (match, content) => {
    const mark = `<think-snippet>${sha256(content.trim())}</think-snippet>`
    if (matches.length === 0) {
      matches.push(content)
      return mark + match
    }
    matches.push(content)
    return mark
  })

  if (matches.length > 0) {
    const firstIndex = result.indexOf('<juchats-thinking>')
    const lastIndex = result.lastIndexOf('<juchats-thinking>')
    if (lastIndex > firstIndex) {
      result = result.slice(0, lastIndex)
    }
  }

  return {
    markdown: result,
    matches: matches.map(item => item.trim()),
  }
}

export const resolveStaticPath = (path: string) => {
  if (process.env && process.env.STORYBOOK) {
    return path.replace('share/', '')
  }
  return path
}

/**
 * 处理video标签中的mp4 URL去重
 * @param content 要处理的内容
 * @param foundUrls 已发现的URL集合
 * @param location 当前处理的位置（用于日志）
 * @returns 处理后的内容
 */
function processVideoTags(content: string, foundUrls: Set<string>, location: string): string {
  const mp4UrlRegex = /(https?:\/\/[^\s<>()[\]{}'"]+\.mp4)/gi

  // 匹配所有video标签（包括自闭合和完整标签）
  const videoTagRegex = /<video[^>]*>[\s\S]*?<\/video>|<video[^>]*\/>/gi

  return content.replace(videoTagRegex, (videoTag) => {
    // 提取video标签中的所有mp4 URL
    const urls = Array.from(videoTag.matchAll(mp4UrlRegex)).map(match => match[0])

    if (urls.length === 0) {
      // 没有mp4 URL，保留video标签
      return videoTag
    }

    // 检查这个video标签中的URL是否都是重复的
    const allUrlsDuplicated = urls.every(url => foundUrls.has(url))

    if (allUrlsDuplicated) {
      // 所有URL都是重复的，删除整个video标签
      return ''
    }

    // 有新的URL，记录并保留video标签
    urls.forEach((url) => {
      if (!foundUrls.has(url)) {
        foundUrls.add(url)
      }
    })

    return videoTag
  })
}

/**
 * 去重 mp4 URL 的函数
 * 会删除重复的包含 mp4 URL 的 video 标签
 * 支持处理特殊的 XML 标签（juchats-thinking、juchats-quote、juchats-artifacts、think-snippet）
 * @param content 要处理的内容
 * @returns 去重后的内容
 */
export function deduplicateMp4Urls(content: string): string {
  const foundUrls = new Set<string>()

  // 定义需要单独处理的标签
  const specialTags = [
    { name: 'juchats-thinking', regex: /(<juchats-thinking>)([\s\S]*?)(<\/juchats-thinking>)/g },
    { name: 'juchats-quote', regex: /(<juchats-quote>)([\s\S]*?)(<\/juchats-quote>)/g },
    { name: 'juchats-artifacts', regex: /(<juchats-artifacts>)([\s\S]*?)(<\/juchats-artifacts>)/g },
    { name: 'think-snippet', regex: /(<think-snippet>)([\s\S]*?)(<\/think-snippet>)/g },
  ]

  // 存储标签内容的占位符映射
  const placeholders = new Map<string, string>()
  let placeholderIndex = 0
  let processedContent = content

  // 提取所有特殊标签的内容，用占位符替换
  for (const tag of specialTags) {
    processedContent = processedContent.replace(tag.regex, (_match, openTag, innerContent, closeTag) => {
      const placeholder = `__PLACEHOLDER_${placeholderIndex++}__`
      // 对标签内的内容处理video标签和URL
      let deduplicatedInner = innerContent

      // 先处理video标签中的重复URL
      deduplicatedInner = processVideoTags(deduplicatedInner, foundUrls, `${tag.name}内`)

      placeholders.set(placeholder, openTag + deduplicatedInner + closeTag)
      return placeholder
    })
  }

  // 对标签外的内容进行去重
  processedContent = processVideoTags(processedContent, foundUrls, '标签外')

  // 将占位符还原为处理后的标签内容
  for (const [placeholder, originalContent] of placeholders) {
    processedContent = processedContent.replace(placeholder, originalContent)
  }

  return processedContent
}
